import type { FC, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import ImpactRatingForm from 'src/pages/impacts/ratings/forms/impact-rating-form';
import type { ImpactRatingFormFieldData } from 'src/pages/impacts/ratings/forms/impact-rating-form/impactRatingFormSchema';
import type { ObjectWithContributors } from 'src/rbac/Permission';

import { useInsertImpactInternalAuditRating } from '@/hooks/mutations/internal-audit-result/useInsertImpactInternalAuditRating';
import { useGetInternalAuditImpactRatingById } from '@/hooks/queries';
import { internalAuditReportResultsUrl } from '@/utils/urls';

type Props = {
  readonly: boolean;
  navigateToResults: boolean;
  assessmentId: string;
  assessedItem?: ObjectWithContributors;
  id?: string;
  onDismiss?: (saved: boolean) => void;
  beforeFieldsSlot?: ReactNode;
  header?: string;
};

const ConnectedImpactRatingResultForm: FC<Props> = ({
  readonly,
  assessmentId,
  id,
  onDismiss,
  beforeFieldsSlot,
  navigateToResults,
  header,
}) => {
  const navigate = useNavigate();

  const { data } = useGetInternalAuditImpactRatingById({
    queryArgs: { id: id! },
    shouldSkip: !id,
  });
  const impactRating = data?.impact_internal_audit_rating?.[0];

  const { insertImpactInternalAuditRating: insertImpactRating } =
    useInsertImpactInternalAuditRating();

  const onSave = async (values: ImpactRatingFormFieldData) => {
    const result = await insertImpactRating({
      InternalAuditReportId: assessmentId,
      RatedItemId: values.RatedItemId,
      Ratings: [
        {
          ImpactId: values.ImpactId,
          Rating: values.Rating,
        },
      ],
      TestDate: values.TestDate,
      CompletedBy: values.CompletedBy?.value,
      CustomAttributeData: values.CustomAttributeData ?? null,
    });

    if (!result.insertChildImpactInternalAuditRating?.Ids) {
      throw new Error('Impact rating result id is missing');
    }

    if (navigateToResults) {
      navigate(internalAuditReportResultsUrl(assessmentId));
    }
  };

  return (
    <div className={'pb-5'}>
      <ImpactRatingForm
        ratingContext={'internal_audit'}
        header={header}
        values={
          impactRating
            ? {
                Rating: impactRating.Rating,
                ImpactId: impactRating.ImpactId,
                RatedItemId: impactRating.RatedItemId,
                TestDate: impactRating.TestDate,
                CustomAttributeData: impactRating.CustomAttributeData,
                CompletedBy: impactRating.CompletedBy
                  ? { value: impactRating.CompletedBy, type: 'user' }
                  : null,
              }
            : undefined
        }
        onSave={onSave}
        readOnly={readonly}
        onDismiss={onDismiss}
        renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
        beforeFieldsSlot={beforeFieldsSlot}
      />
    </div>
  );
};

export default ConnectedImpactRatingResultForm;
